import { Link } from 'react-router-dom';
import {
  MapPin,
  Buildings,
  CalendarCheck,
  Phone,
  ArrowRight,
  Globe,
  UsersThree,
  CaretRight,
} from '@phosphor-icons/react';
import { BRAND, COVERAGE } from '../lib/brand';
import { COUNTIES, CITIES, HOSPITAL_NETWORKS } from '../lib/serviceAreas';

export const AreasWeServePage = () => {
  return (
    <main data-testid="areas-we-serve-page">
      {/* Hero */}
      <section className="bg-[#800000] py-32 pt-40 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage: 'radial-gradient(circle, #D4AF37 1px, transparent 1px)',
            backgroundSize: '32px 32px',
          }}
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <nav className="flex items-center gap-2 text-xs text-white/70 mb-6 tracking-wide">
            <Link to="/" className="hover:text-[#D4AF37] transition-colors">Home</Link>
            <CaretRight size={10} weight="bold" />
            <span className="text-[#D4AF37]">Areas We Serve</span>
          </nav>
          <div className="inline-flex items-center gap-3 mb-6">
            <div className="h-px w-10 bg-[#D4AF37]" />
            <span className="text-[#D4AF37] text-xs font-semibold tracking-[0.3em] uppercase">
              {COVERAGE.headline}
            </span>
          </div>
          <h1 className="font-serif-display text-5xl sm:text-6xl font-semibold text-white mb-5 leading-[1.05]">
            Areas We <span className="italic gold-shimmer">Serve.</span>
          </h1>
          <p className="text-lg text-white/85 max-w-2xl leading-relaxed">
            India's mobile notary service covers every county, city, and ZIP code across Middle Tennessee — plus a nationwide signing-agent network and Tennessee-compliant Remote Online Notarization for clients anywhere in the world.
          </p>
        </div>
      </section>

      {/* 3-tier coverage strip */}
      <section className="py-16 bg-[#FBF8F1] border-b border-[#E8E2D5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-5">
            {COVERAGE.tiers.map((tier, i) => {
              const Icon = i === 0 ? MapPin : i === 1 ? UsersThree : Globe;
              return (
                <div
                  key={i}
                  className="bg-white border border-[#E8E2D5] rounded-sm p-6 flex items-start gap-4"
                >
                  <div className="w-12 h-12 rounded-full bg-[#800000]/5 flex items-center justify-center flex-shrink-0">
                    <Icon weight="duotone" size={24} className="text-[#800000]" />
                  </div>
                  <div>
                    <p className="text-[10px] tracking-[0.25em] uppercase text-[#D4AF37] font-bold mb-1">
                      {tier.label}
                    </p>
                    <p className="font-serif-display text-lg text-[#800000] font-semibold leading-tight mb-1">
                      {tier.area}
                    </p>
                    <p className="text-[#5C5C5C] text-xs leading-relaxed">{tier.detail}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Counties grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12">
            <p className="text-[#D4AF37] text-xs font-semibold tracking-[0.3em] uppercase mb-3">
              By County
            </p>
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#800000] mb-3 leading-tight">
              Middle Tennessee <span className="italic">counties.</span>
            </h2>
            <p className="text-[#5C5C5C] text-base max-w-2xl">
              Click any county for local notary, apostille, loan-signing, and estate-planning details.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {COUNTIES.map((county) => (
              <Link
                key={county.slug}
                to={`/areas/county/${county.slug}`}
                data-testid={`hub-county-${county.slug}`}
                className="group relative bg-white border border-[#E8E2D5] hover:border-[#800000]/30 rounded-sm overflow-hidden hover:shadow-2xl hover:-translate-y-0.5 transition-all"
              >
                <div className="relative h-40 overflow-hidden">
                  <img
                    src={county.heroImage}
                    alt={`${county.name}, Tennessee`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#800000]/85 via-[#800000]/40 to-transparent" />
                  <div className="absolute bottom-3 left-5 right-5">
                    <p className="text-[10px] tracking-[0.25em] uppercase text-[#D4AF37] font-bold mb-0.5">
                      Seat: {county.seat}
                    </p>
                    <h3 className="font-serif-display text-2xl text-white font-semibold leading-tight">
                      {county.name}
                    </h3>
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-[#5C5C5C] text-sm leading-relaxed mb-4 line-clamp-3">
                    {county.intro.slice(0, 165)}…
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-[#5C5C5C]">
                      {county.population} residents · {county.cityCount}+ cities
                    </span>
                    <span className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-[#800000] group-hover:text-[#D4AF37] transition-colors">
                      Visit <ArrowRight weight="bold" size={12} className="group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Hospital networks section */}
      <section data-testid="hospital-networks-section" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12">
            <p className="text-[#D4AF37] text-xs font-semibold tracking-[0.3em] uppercase mb-3">
              Bedside & Hospital Coverage
            </p>
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#800000] mb-3 leading-tight">
              Hospital networks <span className="italic">we cover.</span>
            </h2>
            <p className="text-[#5C5C5C] text-base max-w-3xl leading-relaxed">
              India is a regular bedside notary across Middle Tennessee's three largest health systems — Ascension Saint Thomas, TriStar Health, and Vanderbilt University Medical Center. Case managers, social workers, and family caregivers can call directly to schedule same-day visits for healthcare directives, POAs, and estate signings.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {HOSPITAL_NETWORKS.map((h) => (
              <div
                key={h.slug}
                data-testid={`hospital-network-${h.slug}`}
                className="bg-[#FBF8F1] border border-[#E8E2D5] hover:border-[#D4AF37]/50 hover:shadow-lg rounded-sm p-7 transition-all"
              >
                <h3 className="font-serif-display text-xl font-semibold text-[#800000] mb-3 leading-tight">
                  {h.name}
                </h3>
                <p className="text-[#5C5C5C] text-sm leading-relaxed mb-5">
                  {h.blurb}
                </p>
                <ul className="space-y-1.5 text-[#1A1A1A] text-xs leading-relaxed border-t border-[#E8E2D5] pt-4">
                  {h.locations.map((loc, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-[#D4AF37] font-bold mt-0.5">·</span>
                      <span>{loc}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cities grid */}
      <section className="py-20 bg-[#FBF8F1]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12">
            <p className="text-[#D4AF37] text-xs font-semibold tracking-[0.3em] uppercase mb-3">
              By City
            </p>
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#800000] mb-3 leading-tight">
              Major cities & <span className="italic">neighborhoods.</span>
            </h2>
            <p className="text-[#5C5C5C] text-base max-w-2xl">
              Same-day mobile notary in every major city across Davidson, Williamson, Sumner,
              Rutherford, Wilson, and Maury counties.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {CITIES.map((city) => (
              <Link
                key={city.slug}
                to={`/areas/city/${city.slug}`}
                data-testid={`hub-city-${city.slug}`}
                className="group bg-white border border-[#E8E2D5] hover:border-[#800000]/40 hover:shadow-lg rounded-sm p-5 transition-all"
              >
                <div className="flex items-start gap-2 mb-2">
                  <Buildings weight="duotone" size={20} className="text-[#800000] flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-serif-display text-base font-semibold text-[#800000] leading-tight group-hover:text-[#5C0000]">
                      {city.name}
                    </h3>
                    <p className="text-[10px] text-[#5C5C5C] tracking-wide mt-0.5">
                      {city.countyName.split('(')[0].trim()}
                    </p>
                  </div>
                </div>
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-[#800000] group-hover:text-[#D4AF37] mt-2">
                  Notary in {city.name} <ArrowRight weight="bold" size={10} className="group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-[#800000] relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: 'radial-gradient(circle, #D4AF37 1px, transparent 1px)',
            backgroundSize: '32px 32px',
          }}
        />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-white mb-5 leading-tight">
            Don't see your <span className="italic gold-shimmer">city or county?</span>
          </h2>
          <p className="text-base sm:text-lg text-white/85 mb-9 max-w-2xl mx-auto leading-relaxed">
            India regularly serves areas across Middle Tennessee — and beyond, through her
            nationwide signing-agent network and worldwide RON service. Call her directly
            and she'll get you sorted.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/booking"
              data-testid="hub-book-btn"
              className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#1A1A1A] hover:bg-[#F5E6A8] transition-all px-7 py-4 rounded-sm font-semibold text-sm uppercase tracking-[0.15em]"
            >
              <CalendarCheck weight="bold" size={18} />
              Book a Notary
            </Link>
            <a
              href={`tel:${BRAND.phoneRaw}`}
              className="inline-flex items-center justify-center gap-2 border-2 border-white text-white hover:bg-white hover:text-[#800000] transition-all px-7 py-4 rounded-sm font-semibold text-sm"
            >
              <Phone weight="bold" size={18} />
              {BRAND.phone}
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};
